import Vue from 'vue';
import { UiError } from '@omnica/error-vue2';
import { UiPopper } from '@omnica/popper-vue2';
import { ALIGN } from '@omnica/error';
import { APPEARANCE, SIZE } from '@omnica/input';
import { PLACEMENT } from '@omnica/popper';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
  function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
  return new (P || (P = Promise))(function (resolve, reject) {
      function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
      function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
      function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
      step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
  var e = new Error(message);
  return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var OmnicaIconClearCircle = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"viewBox":"0 0 24 24","xmlns":"http://www.w3.org/2000/svg"}},[_c('path',{attrs:{"d":"M4 12a8 8 0 1116 0 8 8 0 01-16 0zm11 2.045c0-.122-.05-.24-.14-.324L13.148 12l1.712-1.721a.445.445 0 000-.648l-.49-.49a.444.444 0 00-.649 0L12 10.851 10.279 9.14a.444.444 0 00-.648 0l-.49.49a.444.444 0 000 .649L10.851 12 9.14 13.721a.444.444 0 000 .648l.49.49a.444.444 0 00.649 0L12 13.149l1.721 1.712a.444.444 0 00.648 0l.49-.49a.445.445 0 00.141-.325z","fill":"currentColor","fill-rule":"evenodd","clip-rule":"evenodd"}})]) } };

var OmnicaIconLoading = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{staticStyle:{"padding":"2px"},attrs:{"xmlns":"http://www.w3.org/2000/svg","version":"1.0","fill":"currentColor","viewBox":"0 0 128 128"}},[_c('g',{attrs:{"transform":"matrix(-1 0 0 1 128 0)"}},[_c('circle',{attrs:{"cx":"16","cy":"64","r":"13"}}),_c('circle',{attrs:{"cx":"16","cy":"64","r":"11.344","transform":"rotate(45 64 64)"}}),_c('circle',{attrs:{"cx":"16","cy":"64","r":"9.531","transform":"rotate(90 64 64)"}}),_c('circle',{attrs:{"cx":"16","cy":"64","r":"7.75","transform":"rotate(135 64 64)"}}),_c('circle',{attrs:{"cx":"16","cy":"64","r":"7.063","transform":"rotate(180 64 64)"}}),_c('circle',{attrs:{"cx":"16","cy":"64","r":"5.063","transform":"rotate(225 64 64)"}}),_c('circle',{attrs:{"cx":"16","cy":"64","r":"4.438","transform":"rotate(270 64 64)"}}),_c('circle',{attrs:{"cx":"16","cy":"64","r":"3.375","transform":"rotate(315 64 64)"}}),_c('animateTransform',{attrs:{"attributeName":"transform","type":"rotate","values":"0 64 64;315 64 64;270 64 64;225 64 64;180 64 64;135 64 64;90 64 64;45 64 64;","calcMode":"discrete","dur":"960ms","repeatCount":"indefinite"}})],1)]) } };

const componentExists = (name) => {
    return name in Vue.options.components;
};

const ALLOWED_NUMBER_CODES = [8, 46, 13, 37, 39];
const DECIMAL_SEPARATORS = [',', '.'];
const USER_AGENT = navigator.userAgent;
const IS_ANDROID = USER_AGENT.toLowerCase().includes('android');
const IS_APPLE = /Apple Computer/.test(navigator.vendor);
const IS_DESKTOP_SAFARI = /Safari/i.test(USER_AGENT) && !/Mobi|Android/i.test(USER_AGENT) && IS_APPLE;
const IS_FIREFOX = USER_AGENT.search(/Firefox/) > 0;
const isEmpty = (value) => String(value).length === 0;
const isInteger = (value) => !isNaN(parseInt(String(value)));
const isNumeric = (value) => !isNaN(parseFloat(String(value)));
const isSeparator = (char) => DECIMAL_SEPARATORS.some(d => d === char);
const observeIntersections = (el, handler) => {
    if (window.IntersectionObserver) {
        const observer = new window.IntersectionObserver(([entry]) => {
            if (entry.intersectionRatio > 0) {
                handler();
            }
        });
        observer.observe(el);
        return observer;
    }
    return undefined;
};
const observeMutations = (el, handler) => {
    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            if (['childList', 'subtree'].includes(mutation.type)) {
                handler();
            }
        }
    });
    observer.observe(el, {
        childList: true,
        subtree: true,
    });
    return observer;
};
let counter = 0;
var script = Vue.extend({
    name: 'UiInput',
    components: {
        OmnicaIconClearCircle,
        OmnicaIconLoading,
        UiPopper,
        UiError,
    },
    provide() {
        return {
            __OmnicaTextField_insert: this.insert.bind(this),
        };
    },
    props: {
        /** Атрибут id нативного поля ввода input. Должен быть уникальным на странице */
        id: {
            type: String,
            default: () => `ui-input-${++counter}`,
        },
        /** Атрибут name нативного поля ввода input */
        name: {
            type: String,
            default: null,
        },
        /** Атрибут type нативного поля ввода input */
        type: {
            type: String,
            default: 'text',
        },
        /** Значение поля ввода */
        value: {
            type: [String, Number],
            default: '',
        },
        /** Атрибут autocomplete нативного поля ввода input */
        autocomplete: {
            type: String,
            validator: (value) => ['on', 'off'].includes(value),
            default: 'off',
        },
        /** Атрибут placeholder нативного поля ввода input */
        placeholder: {
            type: String,
            default: '',
        },
        /** Атрибут max-length нативного поля ввода input */
        maxlength: {
            type: [String, Number],
            default: null,
        },
        /** Атрибут max нативного поля ввода type="number" */
        max: {
            type: [String, Number],
            default: Infinity,
        },
        /** Атрибут min нативного поля ввода type="number" */
        min: {
            type: [String, Number],
            default: -Infinity,
        },
        /** Атрибут step нативного поля ввода type="number" */
        step: {
            type: [String, Number],
            default: null,
        },
        /** Ввод только целых значений */
        integer: {
            type: Boolean,
            default: false,
        },
        /**
         * Разделитель дробной части (. или ,)
         * Имеет смысл только для input[type=number]
         */
        decimalSeparator: {
            type: String,
            validator: (value) => DECIMAL_SEPARATORS.some(el => el === value),
            default: null,
        },
        /** Максимальное количество знаков после запятой; имеет смысл только для input[type=number] */
        maxDecimalPlaces: {
            type: Number,
            default: null,
        },
        /** Атрибут required нативного поля ввода input */
        required: {
            type: Boolean,
            default: false,
        },
        /** Атрибут readonly нативного поля ввода input */
        readonly: {
            type: Boolean,
            default: false,
        },
        /** Заблокированный */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Текст ошибки, выводимый при выходе значения
         * за пределы допустимого диапазона для input[type='number']
         */
        outOfRangeErrorText: {
            type: [String, Function],
            default: null,
        },
        /** Массив ошибок */
        errors: {
            type: Array,
            default: () => [],
        },
        /** Определяет, по какому краю будет выровнен текст ошибки */
        errorsTextAlign: {
            type: String,
            validator: (align) => Object.values(ALIGN).includes(align),
            default: ALIGN.LEFT,
        },
        /** Определяет, будет ли текст ошибок обрезаться через многоточие или переноситься на следующую строку */
        errorsTextEllipsis: {
            type: Boolean,
            default: false,
        },
        /** Показывать ошибки во всплывающей подсказке, если поле не outline или недостаточно длинное */
        errorsInTooltip: {
            type: Boolean,
            default: false,
        },
        /** Класс всплывающей подсказки с ошибкой */
        errorsTooltipClass: {
            type: String,
            default: '',
        },
        /** Контейнер всплывающей подсказки с ошибкой */
        errorsTooltipContainer: {
            type: null,
            validator: (value) => value === null || value instanceof Element || typeof value === 'string',
            default: null,
        },
        /** Позиционирование всплывающей подсказки с ошибкой */
        errorsTooltipPlacement: {
            type: String,
            default: PLACEMENT.RIGHT,
        },
        /** Отключить эмуляцию управления значением числового поля ввода с помощью стрелок на клавиатуре */
        disableArrows: {
            type: Boolean,
            default: false,
        },
        /** Внешний вид */
        appearance: {
            type: String,
            validator: (appearance) => Object.values(APPEARANCE).includes(appearance),
            default: APPEARANCE.OUTLINED,
        },
        /** Иконка перед полем ввода input */
        leadingIcon: {
            type: String,
            default: '',
        },
        /** Будет изменяться заливка левой иконки, в зависимости от состояния поля */
        leadingIconFill: {
            type: Boolean,
            default: true,
        },
        /** Иконка после поля ввода input */
        trailingIcon: {
            type: String,
            default: '',
        },
        /** Будет изменяться заливка правой иконки, в зависимости от состояния поля */
        trailingIconFill: {
            type: Boolean,
            default: true,
        },
        /** Сфокусированный внешний вид */
        active: {
            type: Boolean,
            default: false,
        },
        /** Иконка или текст вида "https:" перед полем ввода */
        prefix: {
            type: String,
            default: null,
        },
        /** Отображается процесс загрузки в виде спиннера */
        loading: {
            type: Boolean,
            default: false,
        },
        /** Отображает иконку сброса в виде крестика */
        clearable: {
            type: Boolean,
            default: false,
        },
        /** Включает автоматическое изменение ширины по мере ввода текста */
        fitContent: {
            type: Boolean,
            default: false,
        },
        /**
         * Минимальное значение поля ввода (без учета иконок и т.п)
         * Для fitContent минимальная ширина устанавливается
         * по ширине placeholder'а. Это свойство позволяет это переопределить
         */
        minWidth: {
            type: Number,
            default: null,
        },
        /** Будет установлен фокус при монтировании элемента */
        withFocus: {
            type: Boolean,
            default: false,
        },
        /** Выделить содержимое поля ввода при получении фокуса */
        selectOnFocus: {
            type: Boolean,
            default: false,
        },
        /** Подсказывает браузерам для устройств с экранной клавиатурой, какой вариант демонстрировать */
        inputmode: {
            type: String,
            default: null,
        },
        /** Атрибут pattern нативного поля ввода input */
        pattern: {
            type: String,
            default: null,
        },
        /** Жирное начертание */
        accent: {
            type: Boolean,
            default: false,
        },
        /** Размер поля ввода. Сейчас применяется только для appearance === 'inline' */
        size: {
            type: String,
            validator: (size) => Object.values(SIZE).includes(size),
            default: SIZE.SM,
        },
        /**
         * Инвертированные цвета. Сейчас применяется
         * только для appearance === 'inline'
         */
        inversion: {
            type: Boolean,
            default: false,
        },
        /**
         * Снятие фокуса с поля ввода при нажатии Enter и Esc
         * При снятии фокуса происходит выпуск события input-save, если снятие вызвано не нажатием ESC
         * (Логика в ДС описана для InlineEdit)
         */
        saveOnBlur: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            snapshot: '',
            width: 0,
            usedDecimalSeparator: this.decimalSeparator,
            hasLeadingIcon: false,
            hasTrailingIcon: false,
            focused: false,
            disposed: false,
        };
    },
    computed: {
        /**
         * Определяет, требуется ли обработка числового значения
         * Например, кастомный разделитель или ограничение количества знаков после запятой
         */
        hasNumericFormatting() {
            // @TODO для Chrome на Android не срабатывает prevent в keydown/keydown, пока отключаем эти доп. возможности
            return !IS_ANDROID && (!!this.maxDecimalPlaces || !!this.decimalSeparator || this.integer);
        },
        hasIconComponent: () => componentExists('UiIcon'),
        hasPrefix() {
            return this.isOutlined && (!!this.prefix || !!this.$slots.prefix);
        },
        invalid() {
            return this.isOutOfRange || this.errorList.length > 0;
        },
        isInline() {
            return this.appearance === APPEARANCE.INLINE;
        },
        isOutlined() {
            return this.appearance === APPEARANCE.OUTLINED;
        },
        isShort() {
            return this.width > 0 && this.width < 192;
        },
        errorList() {
            return [
                ...(this.isOutOfRange && this.outOfRangeErrorText ? [this.outOfRangeErrorText] : []),
                ...this.errors,
            ];
        },
        isOutOfRange() {
            return !this.focused
                && !isEmpty(this.value)
                && this.isNumericType
                && isNumeric(this.value)
                && (this.value > this.max || this.value < this.min);
        },
        /** Определяет, является ли поле ввода числовым */
        isNumericType() {
            return this.type === 'number' || this.type === 'decimal' || this.hasNumericFormatting;
        },
        valueComputed() {
            var _a, _b;
            if (this.value === null) {
                return this.value;
            }
            let value = String(this.value);
            if (this.isMaximumOfDecimalPlacesExceeded(value)) {
                const numericPattern = new RegExp('[0-9]*.[0-9]{' + this.maxDecimalPlaces + '}');
                const numericMatches = (_a = value.match(numericPattern)) !== null && _a !== void 0 ? _a : [];
                value = (_b = numericMatches[0]) !== null && _b !== void 0 ? _b : '';
            }
            if (this.hasNumericFormatting && this.usedDecimalSeparator === ',') {
                value = value.toString().replace('.', ',');
            }
            return value;
        },
    },
    watch: {
        value(curr, prev) {
            return __awaiter(this, void 0, void 0, function* () {
                const input = this.$refs.input;
                /**
                 * При v-model.number Vue приводит значение в модели к числу.
                 * Из-за этого стирается разделитель при удалении десятых (когда стираем backspace).
                 */
                if (input && this.hasNumericFormatting && !/\d*\./.test(curr) && /\d*\.\d$/.test(prev)) {
                    const value = input.value;
                    yield this.$nextTick();
                    input.value = value;
                }
                this.adjustWidth();
            });
        },
        fitContent(fitContent) {
            return __awaiter(this, void 0, void 0, function* () {
                yield this.$nextTick();
                const input = this.$refs.input;
                if (fitContent) {
                    this.adjustWidth();
                    return;
                }
                input.style.width = '';
                // eslint-disable-next-line no-unused-expressions,@typescript-eslint/no-unused-expressions
                input.clientWidth; // force redraw
                /** Событие срабатывает после изменения ширины поля ввода */
                this.$emit('input-resize');
            });
        },
        decimalSeparator: {
            immediate: true,
            handler(decimalSeparator) {
                this.usedDecimalSeparator = decimalSeparator;
            },
        },
        maxDecimalPlaces() {
            this.adjustWidth();
        },
    },
    created() {
        this.hasLeadingIcon = !!this.$slots['leading-icon'] || !!this.leadingIcon;
        this.hasTrailingIcon = !!this.$slots['trailing-icon'] || !!this.trailingIcon;
        if (this.saveOnBlur) {
            this.snapshot = this.value;
        }
    },
    mounted() {
        return __awaiter(this, void 0, void 0, function* () {
            this.updateWidth();
            this.adjustWidth();
            const context = this;
            context.__OmnicaInputIntersectionObserver = observeIntersections(this.$refs.input, () => {
                this.adjustWidth();
                this.updateWidth();
            });
            context.__OmnicaInputMutationObserver = observeMutations(this.$refs.root, () => {
                this.updateFlags();
            });
            window.addEventListener('resize', this.updateWidth);
            if (this.withFocus) {
                yield this.$nextTick();
                this.focus();
            }
        });
    },
    beforeDestroy() {
        var _a, _b;
        window.removeEventListener('resize', this.updateWidth);
        const context = this;
        (_a = context.__OmnicaInputIntersectionObserver) === null || _a === void 0 ? void 0 : _a.disconnect();
        context.__OmnicaInputIntersectionObserver = undefined;
        (_b = context.__OmnicaInputMutationObserver) === null || _b === void 0 ? void 0 : _b.disconnect();
        context.__OmnicaInputMutationObserver = undefined;
        this.disposed = true;
    },
    methods: {
        /** @public */
        isOverflown() {
            const input = this.$refs.input;
            return !!input && input.scrollWidth > input.clientWidth;
        },
        clear(event = undefined) {
            if (!this.disabled) {
                /** Срабатывает при вводе значения в поле ввода */
                this.$emit('input', '');
                /** Событие срабатывает при сбросе значения инпута */
                this.$emit('clear', event);
            }
        },
        /** @public */
        focus() {
            const input = this.$refs.input;
            input === null || input === void 0 ? void 0 : input.focus();
        },
        /** @public */
        blur() {
            const input = this.$refs.input;
            input === null || input === void 0 ? void 0 : input.blur();
        },
        /** @public */
        select() {
            const input = this.$refs.input;
            input === null || input === void 0 ? void 0 : input.select();
        },
        /** @public */
        getInputWidth() {
            return __awaiter(this, void 0, void 0, function* () {
                const inner = this.$refs.inner;
                if (!inner) {
                    return 0;
                }
                const contentWidth = yield this.getInputContentWidth();
                const style = getComputedStyle(inner);
                const pl = parseFloat(style.paddingLeft) || 0;
                const pr = parseFloat(style.paddingRight) || 0;
                const bl = parseFloat(style.borderLeft) || 0;
                const br = parseFloat(style.borderRight) || 0;
                return contentWidth + pl + pr + bl + br;
            });
        },
        /** @public */
        getSelectionStart() {
            var _a;
            const input = this.$refs.input;
            return (_a = input === null || input === void 0 ? void 0 : input.selectionStart) !== null && _a !== void 0 ? _a : null;
        },
        /** @public */
        setSelectionRange(start, end) {
            const input = this.$refs.input;
            input === null || input === void 0 ? void 0 : input.setSelectionRange(start, end);
        },
        /** @public */
        setValue(value) {
            const input = this.$refs.input;
            if (input) {
                input.value = value;
            }
        },
        onInput(event) {
            const char = event.data;
            const input = event.target;
            let value = input.value;
            if (this.hasNumericFormatting) {
                value = value.replace(',', '.');
                if (this.isMaximumOfDecimalPlacesExceeded(value)) {
                    input.value = this.valueComputed;
                    this.adjustWidth();
                    return;
                }
                if (Number(value) === Number(this.value)
                    && isSeparator(char)
                    && this.usedDecimalSeparator !== char) {
                    input.value = value.replace(char, this.usedDecimalSeparator);
                    this.adjustWidth();
                    return;
                }
            }
            this.$emit('input', value);
            this.adjustWidth();
        },
        onChange(event) {
            const context = this;
            const value = event.target.value;
            if (this.snapshot !== value && !context.__OmnicaInputEsc && this.saveOnBlur) {
                this.snapshot = value;
            }
            this.$emit('change', value);
        },
        onFocus(e = undefined) {
            return __awaiter(this, void 0, void 0, function* () {
                const input = this.$refs.input;
                this.focused = true;
                if (input && !this.readonly && (this.selectOnFocus || this.isInline)) {
                    input.scrollLeft = input.scrollWidth;
                    yield this.$nextTick();
                    input.select();
                }
                /** Срабатывает при установке фокуса на поле ввода */
                this.$emit('focus', e);
            });
        },
        onBlur(event) {
            const context = this;
            const input = event.target;
            this.focused = false;
            /** Срабатывает при потере фокуса полем ввода */
            this.$emit('blur', event);
            if ((this.saveOnBlur || this.isInline) && !context.__OmnicaInputEsc) {
                /** При снятии фокуса происходит выпуск события input-save, если снятие вызвано не нажатием ESC */
                this.$emit('input-save', input.value);
            }
            if (this.saveOnBlur && context.__OmnicaInputEsc) {
                this.$emit('input', this.snapshot);
            }
            if (context.__OmnicaInputEsc) {
                context.__OmnicaInputEsc = false;
            }
        },
        onKeyDown(event) {
            const input = event.target;
            const [usedSeparator] = DECIMAL_SEPARATORS.filter(el => input.value.includes(el));
            const isAllowedSeparator = !usedSeparator && DECIMAL_SEPARATORS.includes(event.key);
            const isExponent = event.key === 'E' || event.key === 'e'; // Запрет на ввод экспоненты в числовые поля ввода
            const isManualNumberControl = IS_FIREFOX || this.hasNumericFormatting;
            if (!this.decimalSeparator) {
                this.usedDecimalSeparator = usedSeparator
                    ? usedSeparator
                    : isAllowedSeparator
                        ? event.key
                        : null;
            }
            const isAllowed = !isNaN(+event.key)
                || (isAllowedSeparator && !this.integer)
                || ALLOWED_NUMBER_CODES.includes(event.keyCode)
                || event.ctrlKey;
            if (this.isNumericType && ((isManualNumberControl && !isAllowed) || isExponent)) {
                event.preventDefault();
                this.changeValueByArrow(event);
                return false;
            }
            /** Срабатывает, когда клавиша была нажата */
            this.$emit('keydown', event);
            if (!this.saveOnBlur && !this.isInline) {
                return;
            }
            const context = this;
            if (['Enter', 'Escape'].includes(event.key)) {
                context.__OmnicaInputEsc = event.key === 'Escape';
                this.blur();
            }
        },
        insert(text) {
            var _a, _b;
            return __awaiter(this, void 0, void 0, function* () {
                if (!text) {
                    return;
                }
                const input = this.$refs.input;
                const value = this.valueComputed;
                const start = (_a = input.selectionStart) !== null && _a !== void 0 ? _a : 0;
                const end = (_b = input.selectionEnd) !== null && _b !== void 0 ? _b : 0;
                const position = start + text.length;
                this.$emit('input', value.substring(0, start) + text + value.substring(end));
                yield this.$nextTick();
                this.setSelectionRange(position, position);
                this.focus();
            });
        },
        /** Количество знаков после запятой */
        decimalPlaces(value) {
            const [, fraction] = String(value).replace(',', '.').split('.');
            return fraction ? fraction.length : 0;
        },
        /** Меняет значение поля ввода с hasNumericFormatting при нажатии на стрелки. */
        changeValueByArrow(event) {
            const isArrowUp = event.key !== 'ArrowUp';
            const isArrowDown = event.key !== 'ArrowDown';
            if (this.disableArrows || (!isArrowUp && !isArrowDown)) {
                return;
            }
            const step = Number(this.step || 1);
            const oldValue = Number(+this.value || 0);
            const newValue = isArrowUp ? (oldValue + step) : (oldValue - step);
            if ((isArrowUp && newValue <= this.max) || (!isArrowUp && newValue >= this.min)) {
                this.$emit('input', newValue.toFixed(Math.max(this.decimalPlaces(step), this.decimalPlaces(oldValue))));
                this.adjustWidth();
            }
        },
        /** Устанавливает ширину поля ввода в зависимости от содержимого */
        adjustWidth() {
            if (!this.fitContent) {
                this.$emit('input-resize');
                return;
            }
            let minWidth = 0;
            if (isInteger(this.minWidth)) {
                minWidth = this.minWidth;
            }
            else if (this.$refs.placeholder) {
                minWidth = this.$refs.placeholder.clientWidth + 1;
            }
            const input = this.$refs.input;
            input.style.transition = 'none';
            input.style.width = '0px';
            input.style.minWidth = '0px';
            this.$nextTick(() => {
                const input = this.$refs.input;
                const scrollWidth = this.getInputScrollWidth();
                if (minWidth) {
                    input.style.minWidth = this.minWidth + 'px';
                }
                input.style.width = scrollWidth + 'px';
                // eslint-disable-next-line no-unused-expressions,@typescript-eslint/no-unused-expressions
                input.clientWidth; // force redraw
                input.style.transition = '';
                this.$emit('input-resize');
            });
        },
        updateWidth() {
            const inner = this.$refs.inner;
            if (inner) {
                this.width = inner.clientWidth;
            }
        },
        updateFlags() {
            const root = this.$refs.root;
            if (root) {
                this.hasLeadingIcon = !!root.querySelector('.omnica-input__icon_leading');
                this.hasTrailingIcon = !!root.querySelector('.omnica-input__icon_trailing');
            }
        },
        isMaximumOfDecimalPlacesExceeded(value) {
            return this.maxDecimalPlaces !== undefined
                && this.maxDecimalPlaces > 0
                && this.decimalPlaces(value) > this.maxDecimalPlaces;
        },
        getInputContentWidth() {
            return __awaiter(this, void 0, void 0, function* () {
                const input = this.$refs.input;
                const width = input.style.width;
                const transition = input.style.transition;
                input.style.transition = 'none';
                input.style.width = '0px';
                yield this.$nextTick();
                const scrollWidth = this.getInputScrollWidth();
                input.style.width = width;
                // eslint-disable-next-line no-unused-expressions,@typescript-eslint/no-unused-expressions
                input.clientWidth; // force redraw
                input.style.transition = transition;
                return scrollWidth;
            });
        },
        getInputScrollWidth() {
            const input = this.$refs.input;
            let width = input.scrollWidth;
            if (IS_DESKTOP_SAFARI) { // Safari не учитывает padding эл-та
                const style = getComputedStyle(input);
                const pl = parseFloat(style.paddingLeft) || 0;
                const pr = parseFloat(style.paddingRight) || 0;
                width += pl + pr;
            }
            return width + 1;
        },
    },
});

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier /* server only */, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
    if (typeof shadowMode !== 'boolean') {
        createInjectorSSR = createInjector;
        createInjector = shadowMode;
        shadowMode = false;
    }
    // Vue.extend constructor export interop.
    const options = typeof script === 'function' ? script.options : script;
    // render functions
    if (template && template.render) {
        options.render = template.render;
        options.staticRenderFns = template.staticRenderFns;
        options._compiled = true;
        // functional template
        if (isFunctionalTemplate) {
            options.functional = true;
        }
    }
    // scopedId
    if (scopeId) {
        options._scopeId = scopeId;
    }
    let hook;
    if (moduleIdentifier) {
        // server build
        hook = function (context) {
            // 2.3 injection
            context =
                context || // cached call
                    (this.$vnode && this.$vnode.ssrContext) || // stateful
                    (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
            // 2.2 with runInNewContext: true
            if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
                context = __VUE_SSR_CONTEXT__;
            }
            // inject component styles
            if (style) {
                style.call(this, createInjectorSSR(context));
            }
            // register component module identifier for async chunk inference
            if (context && context._registeredComponents) {
                context._registeredComponents.add(moduleIdentifier);
            }
        };
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        options._ssrRegister = hook;
    }
    else if (style) {
        hook = shadowMode
            ? function (context) {
                style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
            }
            : function (context) {
                style.call(this, createInjector(context));
            };
    }
    if (hook) {
        if (options.functional) {
            // register for functional component in vue file
            const originalRender = options.render;
            options.render = function renderWithStyleInjection(h, context) {
                hook.call(context);
                return originalRender(h, context);
            };
        }
        else {
            // inject component registration as beforeCreate hook
            const existing = options.beforeCreate;
            options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
    }
    return script;
}

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function () {
  var _obj;
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      ref: "root",
      class:
        ((_obj = {}),
        (_obj["omnica-input"] = true),
        (_obj["omnica-input_" + _vm.appearance] = true),
        (_obj["omnica-input_" + _vm.size] = true),
        (_obj["omnica-input_accent"] = _vm.inversion && _vm.isInline),
        (_obj["omnica-input_clearable"] = _vm.clearable),
        (_obj["omnica-input_disabled"] = _vm.disabled),
        (_obj["omnica-input_filled"] = !!_vm.value),
        (_obj["omnica-input_fit"] = _vm.fitContent),
        (_obj["omnica-input_focused"] = _vm.focused || _vm.active),
        (_obj["omnica-input_has-leading-icon"] =
          _vm.hasLeadingIcon && !_vm.prefix),
        (_obj["omnica-input_has-trailing-icon"] = _vm.hasTrailingIcon),
        (_obj["omnica-input_invalid"] = _vm.invalid && !_vm.focused),
        (_obj["omnica-input_inverse"] = _vm.inversion && _vm.isInline),
        (_obj["omnica-input_readonly"] = _vm.readonly),
        _obj),
    },
    [
      _c(
        "div",
        {
          ref: "inner",
          staticClass: "omnica-input__inner",
          on: { click: _vm.focus },
        },
        [
          (_vm.$slots["leading-icon"] || _vm.leadingIcon) && !_vm.hasPrefix
            ? _c(
                "div",
                {
                  class: {
                    "omnica-input__icon": true,
                    "omnica-input__icon_leading": true,
                    "omnica-input__icon_fill": _vm.leadingIconFill,
                  },
                },
                [
                  _vm._t("leading-icon", function () {
                    return [
                      _vm.hasIconComponent
                        ? _c("UiIcon", {
                            tag: "component",
                            attrs: { name: _vm.leadingIcon },
                          })
                        : _vm._e(),
                    ]
                  }),
                ],
                2
              )
            : _vm._e(),
          _vm._v(" "),
          _vm.hasPrefix
            ? _c(
                "div",
                { staticClass: "omnica-input__prefix" },
                [
                  _vm._t("prefix", function () {
                    return [
                      _vm._v(
                        "\n                " +
                          _vm._s(_vm.prefix) +
                          "\n            "
                      ),
                    ]
                  }),
                ],
                2
              )
            : _vm._e(),
          _vm._v(" "),
          _c("input", {
            ref: "input",
            staticClass: "omnica-input__input",
            attrs: {
              id: _vm.id,
              type: !_vm.hasNumericFormatting ? _vm.type : "tel",
              name: _vm.name,
              autocomplete: _vm.autocomplete,
              placeholder: _vm.placeholder,
              max:
                _vm.isNumericType && _vm.max !== Infinity ? _vm.max : undefined,
              min:
                _vm.isNumericType && _vm.min !== -Infinity
                  ? _vm.min
                  : undefined,
              step: _vm.isNumericType && _vm.step ? _vm.step : undefined,
              maxlength: _vm.maxlength,
              inputmode: _vm.inputmode,
              pattern: _vm.pattern,
              readonly: _vm.readonly,
              required: _vm.required,
              disabled: _vm.disabled,
            },
            domProps: { value: _vm.valueComputed },
            on: {
              input: _vm.onInput,
              keydown: _vm.onKeyDown,
              change: _vm.onChange,
              focus: _vm.onFocus,
              blur: _vm.onBlur,
              paste: function ($event) {
                return _vm.$emit("paste", $event)
              },
            },
          }),
          _vm._v(" "),
          _vm.clearable && (!!_vm.value || !_vm.fitContent)
            ? _c(
                "div",
                {
                  staticClass: "omnica-input__eraser",
                  on: {
                    click: function ($event) {
                      $event.stopPropagation();
                      return _vm.clear.apply(null, arguments)
                    },
                  },
                },
                [
                  _c("OmnicaIconClearCircle", {
                    directives: [
                      {
                        name: "show",
                        rawName: "v-show",
                        value: !!_vm.value,
                        expression: "!!value",
                      },
                    ],
                    staticClass: "omnica-input__eraser-icon",
                  }),
                ],
                1
              )
            : _vm._e(),
          _vm._v(" "),
          _vm.$slots["addons"] ? _c("div", [_vm._t("addons")], 2) : _vm._e(),
          _vm._v(" "),
          _vm.loading
            ? _c(
                "div",
                { staticClass: "omnica-input__icon" },
                [
                  _c("OmnicaIconLoading", {
                    staticClass: "omnica-input__spinner",
                  }),
                ],
                1
              )
            : _vm.$slots["trailing-icon"] || _vm.trailingIcon
            ? _c(
                "div",
                {
                  class: {
                    "omnica-input__icon": true,
                    "omnica-input__icon_trailing": true,
                    "omnica-input__icon_fill": _vm.trailingIconFill,
                  },
                },
                [
                  _vm._t("trailing-icon", function () {
                    return [
                      _vm.hasIconComponent
                        ? _c("UiIcon", {
                            tag: "component",
                            attrs: { name: _vm.trailingIcon },
                          })
                        : _vm._e(),
                    ]
                  }),
                ],
                2
              )
            : _vm._e(),
          _vm._v(" "),
          _vm.fitContent
            ? _c(
                "div",
                {
                  ref: "refPlaceholder",
                  staticClass: "omnica-input__placeholder",
                },
                [
                  _vm._v(
                    "\n            " + _vm._s(_vm.placeholder) + "\n        "
                  ),
                ]
              )
            : _vm._e(),
        ]
      ),
      _vm._v(" "),
      _vm.errorList.length > 0 && !_vm.focused
        ? [
            !_vm.isShort && _vm.isOutlined
              ? _c(
                  "div",
                  { staticClass: "omnica-input__error-list" },
                  _vm._l(_vm.errorList, function (message, index) {
                    return _c("UiError", {
                      key: index,
                      attrs: {
                        message: message,
                        align: _vm.errorsTextAlign,
                        ellipsis: _vm.errorsTextEllipsis,
                      },
                    })
                  }),
                  1
                )
              : _vm.errorsInTooltip && !_vm.disposed
              ? _c(
                  "UiPopper",
                  {
                    class: [
                      "omnica-input__error-popper",
                      _vm.errorsTooltipClass,
                    ],
                    attrs: {
                      target: function () {
                        return _vm.$refs.root
                      },
                      triggers: [],
                      overflow: ["flip"],
                      container: _vm.errorsTooltipContainer,
                      placement: _vm.errorsTooltipPlacement,
                      dark: !_vm.isInline || !_vm.inversion,
                      shown: "",
                    },
                  },
                  _vm._l(_vm.errorList, function (message, index) {
                    return _c("UiError", {
                      key: index,
                      staticStyle: { color: "inherit" },
                      attrs: {
                        message: message,
                        align: _vm.errorsTextAlign,
                        ellipsis: _vm.errorsTextEllipsis,
                      },
                    })
                  }),
                  1
                )
              : _vm._e(),
          ]
        : _vm._e(),
    ],
    2
  )
};
var __vue_staticRenderFns__ = [];
__vue_render__._withStripped = true;

  /* style */
  const __vue_inject_styles__ = undefined;
  /* scoped */
  const __vue_scope_id__ = undefined;
  /* module identifier */
  const __vue_module_identifier__ = undefined;
  /* functional template */
  const __vue_is_functional_template__ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  );

export { __vue_component__ as UiInput };
